import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppErrorBoundary from '@crm/components/AppErrorBoundary';
import CccLoginForm from '../components/CccLoginForm';
import { getCccLoginConfigStatus } from '../config/loginConfig';
import { CCC_AUTH_FALLBACK, useSafeCccAuth } from '../contexts/CccAuthContext';

const readReturnPath = (location: ReturnType<typeof useLocation>): string => {
  try {
    const from = (location.state as { from?: string } | null)?.from;
    if (from && typeof from === 'string' && from.startsWith('/') && !from.startsWith('/login')) {
      return from;
    }
  } catch {
    /* ignore */
  }
  return '/';
};

const ConfigErrorScreen: React.FC<{ message: string }> = ({ message }) => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      fontFamily: 'Inter, system-ui, sans-serif',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    }}
  >
    <div
      style={{
        maxWidth: 440,
        padding: 28,
        background: '#fff',
        borderRadius: 12,
        border: '1px solid #fecaca',
        boxShadow: '0 4px 24px rgba(15, 23, 42, 0.08)',
      }}
    >
      <h1 style={{ margin: '0 0 12px', fontSize: 20, color: '#991b1b' }}>
        Configuração de login em falta
      </h1>
      <p style={{ margin: 0, fontSize: 14, color: '#64748b', lineHeight: 1.55 }}>
        {message}
      </p>
      <p style={{ margin: '16px 0 0', fontSize: 12, color: '#94a3b8' }}>
        Em produção, confirme que o build inclui <code>.env.production</code> e que o Nginx faz proxy de{' '}
        <code>/api/ccc</code>.
      </p>
    </div>
  </div>
);

/**
 * CCC login — isolated form + CCC backend auth (no CRM Cognito client on render).
 */
const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, hasProvider } = useSafeCccAuth();
  const returnPath = readReturnPath(location);

  const configStatus = useMemo(() => getCccLoginConfigStatus(), []);

  const loginApi = typeof auth?.login === 'function' ? auth.login : CCC_AUTH_FALLBACK.login;
  const setNewPasswordApi =
    typeof auth?.setNewPassword === 'function' ? auth.setNewPassword : CCC_AUTH_FALLBACK.setNewPassword;
  const isLoading = Boolean(auth?.isLoading);
  const isAuthenticated = Boolean(auth?.isAuthenticated);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate(returnPath, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, returnPath]);

  if (!configStatus.ok) {
    return <ConfigErrorScreen message={configStatus.message} />;
  }

  if (!hasProvider) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        <p style={{ color: '#64748b' }}>A iniciar autenticação…</p>
      </div>
    );
  }

  return (
    <AppErrorBoundary scope="Login">
      <CccLoginForm
        auth={{ login: loginApi, setNewPassword: setNewPasswordApi }}
        onSuccess={() => navigate(returnPath, { replace: true })}
      />
    </AppErrorBoundary>
  );
};

export default LoginPage;
